import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Mail, AlertTriangle, Send, Save, Palette } from 'lucide-react';

const TPLS = [
  { key: 'test', label: 'Prueba / Verificación' },
  { key: 'welcome', label: 'Bienvenida (nuevo usuario)' },
  { key: 'invoice', label: 'Factura de licencia' },
  { key: 'reminder', label: 'Recordatorio de pago' },
];

const EmailStudio = () => {
  const { user } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [tpl, setTpl] = useState('test');
  const [to, setTo] = useState(user?.email || '');
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.getEmailConfig().then((r) => setCfg(r.data)).catch(() => {}); }, []);
  if (!cfg) return null;

  const setField = (k, v) => setCfg({ ...cfg, [k]: v });
  const setTplField = (k, v) => setCfg({ ...cfg, templates: { ...cfg.templates, [tpl]: { ...(cfg.templates[tpl] || {}), [k]: v } } });
  const t = cfg.templates[tpl] || {};

  const save = async () => {
    setBusy(true);
    try { const r = await api.saveEmailConfig(cfg); setCfg(r.data); toast.success('SalesPRO: Plantillas de correo guardadas'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error'); } finally { setBusy(false); }
  };
  const sendTest = async () => {
    setBusy(true);
    try { const r = await api.testPlatformEmail(to, tpl); toast.success(`SalesPRO: Correo "${tpl}" enviado a ${r.data.to}`); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error'); } finally { setBusy(false); }
  };

  return (
    <Card data-testid="email-studio"><CardContent className="p-5 space-y-4">
      <h3 className="font-heading font-bold text-[#022C22] flex items-center gap-2"><Mail size={16} /> Correos de SalesPRO — marca y plantillas</h3>
      <p className="text-xs text-[#64748B]">Edita el logo, los colores y el texto de cada tipo de correo que envía la plataforma. Enviados por Resend ({cfg.from_name}).</p>

      {/* Marca */}
      <div className="grid sm:grid-cols-2 gap-3">
        <div><Label className="text-xs flex items-center gap-1"><Palette size={12} /> Logo (URL pública)</Label>
          <Input value={cfg.logo_url || ''} onChange={(e) => setField('logo_url', e.target.value)} placeholder="https://salespr.win/salespro-512.png" data-testid="email-logo-url" /></div>
        <div><Label className="text-xs">Nombre del remitente</Label>
          <Input value={cfg.from_name || ''} onChange={(e) => setField('from_name', e.target.value)} data-testid="email-from-name" /></div>
        <div><Label className="text-xs">Color principal</Label>
          <Input type="color" value={cfg.brand_color || '#022C22'} onChange={(e) => setField('brand_color', e.target.value)} className="h-10 p-1" data-testid="email-brand-color" /></div>
        <div><Label className="text-xs">Firma</Label>
          <Input value={cfg.signature || ''} onChange={(e) => setField('signature', e.target.value)} data-testid="email-signature" /></div>
      </div>
      <label className="flex items-center gap-2 text-xs text-[#475569]">
        <input type="checkbox" checked={cfg.show_dev_mark !== false} onChange={(e) => setField('show_dev_mark', e.target.checked)} data-testid="email-devmark" />
        Mostrar "Desarrollado por 7-study.com" en el pie (marca blanca off)
      </label>

      {/* Plantilla seleccionada */}
      <div className="border-t pt-3 space-y-3">
        <div className="flex flex-wrap gap-2">
          {TPLS.map((x) => (
            <button key={x.key} onClick={() => setTpl(x.key)} data-testid={`tpl-tab-${x.key}`}
              className={`text-xs px-3 py-1.5 rounded-full border ${tpl === x.key ? 'bg-[#022C22] text-white border-[#022C22]' : 'bg-white text-[#475569] border-[#CBD5E1]'}`}>{x.label}</button>
          ))}
        </div>
        <div><Label className="text-xs">Asunto</Label>
          <Input value={t.subject || ''} onChange={(e) => setTplField('subject', e.target.value)} data-testid="tpl-subject" /></div>
        <div><Label className="text-xs">Título (encabezado del correo)</Label>
          <Input value={t.title || ''} onChange={(e) => setTplField('title', e.target.value)} data-testid="tpl-title" /></div>
        <div><Label className="text-xs">Mensaje (admite HTML simple: &lt;p&gt;, &lt;b&gt;)</Label>
          <Textarea rows={5} value={t.body || ''} onChange={(e) => setTplField('body', e.target.value)} data-testid="tpl-body" /></div>
      </div>

      <div className="flex flex-wrap gap-2 items-end border-t pt-3">
        <Button onClick={save} disabled={busy} data-testid="email-save"><Save size={14} className="mr-1" /> Guardar plantillas</Button>
        <div className="flex-1 min-w-[180px]"><Label className="text-xs">Enviar prueba a</Label>
          <Input type="email" value={to} onChange={(e) => setTo(e.target.value)} data-testid="email-test-to" /></div>
        <Button variant="outline" onClick={sendTest} disabled={busy} data-testid="email-test-send"><Send size={14} className="mr-1" /> Enviar esta plantilla</Button>
      </div>
    </CardContent></Card>
  );
};

const DangerZone = () => {
  const [open, setOpen] = useState(false);
  const [phrase, setPhrase] = useState('');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const openDialog = async () => {
    setPhrase(''); setPreview(null); setOpen(true);
    try { const r = await api.factoryReset('BORRAR TODO', true); setPreview(r.data.deleted); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };
  const doReset = async () => {
    if (phrase !== 'BORRAR TODO') return;
    setBusy(true);
    try {
      await api.factoryReset('BORRAR TODO', false);
      toast.success('SalesPRO: Sistema reiniciado. Solo queda el Super Master.');
      setOpen(false);
      setTimeout(() => window.location.reload(), 1200);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setBusy(false); }
  };

  return (
    <Card className="border-red-200" data-testid="danger-zone"><CardContent className="p-5 space-y-3">
      <h3 className="font-heading font-bold text-red-700 flex items-center gap-2"><AlertTriangle size={16} /> Zona de peligro — Reiniciar sistema</h3>
      <p className="text-xs text-[#64748B]">Borra TODAS las empresas, sucursales, clientes, ventas, inventario y vitrinas de prueba, dejando SOLO al Super Master. Úsalo antes de entregar la primera licencia real. Conserva licencia, modelos de planes y tarifas.</p>
      <Button variant="destructive" onClick={openDialog} data-testid="open-reset-dialog">Reiniciar sistema…</Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="reset-dialog">
          <DialogHeader><DialogTitle className="text-red-700 flex items-center gap-2"><AlertTriangle size={18} /> Confirmar reinicio total</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <p>Esto es <b>irreversible</b>. Se eliminará todo lo siguiente:</p>
            {preview ? (
              <div className="bg-red-50 border border-red-200 rounded p-3 text-xs grid grid-cols-2 gap-x-4 gap-y-1 max-h-48 overflow-auto" data-testid="reset-preview">
                <span className="font-semibold">Empresas: {preview.companies ?? 0}</span>
                <span className="font-semibold">Sucursales incluidas ↑</span>
                <span>Clientes: {preview.clients ?? 0}</span>
                <span>Ventas: {preview.sales ?? 0}</span>
                <span>Productos: {preview.products ?? 0}</span>
                <span>Usuarios a borrar: {preview.users_deleted ?? 0}</span>
                <span className="text-green-700 col-span-2">Super Master conservados: {preview.masters_kept ?? 1}</span>
              </div>
            ) : <p className="text-xs text-[#94A3B8]">Calculando…</p>}
            <div>
              <Label className="text-xs">Escribe <b>BORRAR TODO</b> para confirmar</Label>
              <Input value={phrase} onChange={(e) => setPhrase(e.target.value)} placeholder="BORRAR TODO" data-testid="reset-confirm-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button variant="destructive" disabled={phrase !== 'BORRAR TODO' || busy} onClick={doReset} data-testid="confirm-reset-btn">{busy ? 'Borrando…' : 'Sí, borrar todo'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </CardContent></Card>
  );
};

export const SystemOps = () => (<div className="space-y-4"><EmailStudio /><DangerZone /></div>);
export default SystemOps;
